import { createFileRoute, useParams, useRouter } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { AssetChartView } from '@/components/asset/asset-chart-view'
import { AutoBreadcrumb } from '@/components/ui/auto-breadcrumb'
import { AddToWatchlistModal } from '@/features/watchlists/components/AddToWatchlistModal'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { fetchWithAuth } from '@/lib/api'
import { API_BASE } from '@/config/api'
import type { Asset, AssetFundamentals, StockAsset, EtfAsset, FundAsset } from '@/schemas'
import { Loader2, Bookmark } from 'lucide-react'
import { ScreenerService } from '@/features/screener/services/screener-service'
import { KeyMetricsCard } from '@/features/screener/components/KeyMetricsCard'

// Tab components
import { OverviewTab } from '@/features/screener/components/tabs/OverviewTab'
import { FinancialChartsTab } from '@/features/screener/components/tabs/FinancialChartsTab'
import { StatementsTab } from '@/features/screener/components/tabs/StatementsTab'
import { EventsNewsTab } from '@/features/screener/components/tabs/EventsNewsTab'

export const Route = createFileRoute('/_authenticated/screener/$assetId')({
  component: AssetDetailPage,
})

function AssetDetailPage() {
  const { assetId } = useParams({ from: '/_authenticated/screener/$assetId' })
  const router = useRouter()

  const [asset, setAsset] = useState<Asset | null>(null)
  const [variants, setVariants] = useState<Asset[]>([])
  const [fundamentals, setFundamentals] = useState<AssetFundamentals | null>(null)
  const [loading, setLoading] = useState(true)
  const [fundamentalsLoading, setFundamentalsLoading] = useState(true)
  const [watchlistModalOpen, setWatchlistModalOpen] = useState(false)

  const isEnriched = asset?.type === 'stock' || asset?.type === 'etf' || asset?.type === 'fund'
  const enrichedAsset = isEnriched ? (asset as StockAsset | EtfAsset | FundAsset) : null

  useEffect(() => {
    const loadAsset = async () => {
      setLoading(true)
      try {
        // 1. Fetch current asset details
        const res = await fetchWithAuth(`${API_BASE}/assets/${assetId}`)
        if (!res.ok) throw new Error('Asset not found')
        const data = await res.json()
        setAsset(data)

        // 2. If stock/etf, fetch variants
        if (data.type === 'stock' || data.type === 'etf') {
          const isin = data.isin?.trim()
          const hasIsin = Boolean(isin && isin !== '-')
          const searchQuery = data.type === 'stock' ? data.name : hasIsin ? isin : data.symbol
          const searchBy = data.type === 'stock' ? 'name' : hasIsin ? 'isin' : 'symbol'

          const searchUrl = new URL(`${API_BASE}/assets/search`)
          searchUrl.searchParams.append('q', searchQuery)
          searchUrl.searchParams.append('asset_types', data.type)
          searchUrl.searchParams.append('search_by', searchBy)

          const searchRes = await fetchWithAuth(searchUrl.toString())
          if (searchRes.ok) {
            const searchData = await searchRes.json()
            const results: Asset[] = searchData.results || []
            const sameCompanyAssets = results.filter((a) => {
              if (a.asset_id === data.asset_id) {
                return false
              }
              if (data.type === 'stock') {
                return a.type === 'stock' && a.name === data.name
              }
              if (hasIsin && 'isin' in a) {
                return a.isin === isin
              }
              return a.symbol === data.symbol
            })
            setVariants(sameCompanyAssets)
          }
        } else {
          setVariants([])
        }
      } catch (error) {
        console.error('Failed to load asset', error)
      } finally {
        setLoading(false)
      }
    }

    if (assetId) {
      loadAsset()
    }
  }, [assetId])

  // Load fundamentals separately
  useEffect(() => {
    const loadFundamentals = async () => {
      if (!asset || !isEnriched) {
        setFundamentalsLoading(false)
        return
      }

      setFundamentalsLoading(true)
      try {
        const data = await ScreenerService.getAssetFundamentals(asset.asset_id)
        setFundamentals(data)
      } catch (error) {
        console.error('Failed to load fundamentals:', error)
      } finally {
        setFundamentalsLoading(false)
      }
    }

    loadFundamentals()
  }, [asset, isEnriched])

  const handleExchangeChange = (newAssetId: string) => {
    router.navigate({ to: '/screener/$assetId', params: { assetId: newAssetId } })
  }

  if (loading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!asset) {
    return <div>Asset not found</div>
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <AutoBreadcrumb lastSegmentName={asset.name} />
        <Button
          variant="outline"
          onClick={() => setWatchlistModalOpen(true)}
          className="gap-2"
        >
          <Bookmark className="h-4 w-4" />
          Add to Watchlist
        </Button>
      </div>

      {/* Tabs - Dynamic based on asset type */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList variant="line" className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {asset.type === 'stock' && (
            <>
              <TabsTrigger value="financial-charts">Financial Charts</TabsTrigger>
              <TabsTrigger value="statements">Statements</TabsTrigger>
            </>
          )}
          <TabsTrigger value="events-news">Events & News</TabsTrigger>
        </TabsList>


        <TabsContent value="overview">
          <div className="space-y-6">
            <div className={asset.type === 'stock' ? 'grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch' : undefined}>
              <AssetChartView
                asset={asset}
                otherExchanges={variants}
                onExchangeChange={handleExchangeChange}
                className="w-full"
                compact={asset.type === 'stock'}
                showFiveYearPeriod
              />
              {asset.type === 'stock' && enrichedAsset && (
                <KeyMetricsCard asset={enrichedAsset as StockAsset} />
              )}
            </div>
            {/* Overview content displayed for all types */}
            {fundamentalsLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : (
              <OverviewTab 
                asset={asset} 
                enrichedAsset={enrichedAsset} 
                fundamentals={fundamentals}
                showKeyMetrics={asset.type !== 'stock'}
              />
            )}
          </div>
        </TabsContent>

        {asset.type === 'stock' && (
          <TabsContent value="financial-charts">
            <FinancialChartsTab assetId={asset.asset_id} currency={enrichedAsset?.currency_symbol} />
          </TabsContent>
        )}

        {asset.type === 'stock' && (
          <TabsContent value="statements">
            <StatementsTab assetId={asset.asset_id} />
          </TabsContent>
        )}

        <TabsContent value="events-news">
          {fundamentalsLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : (
            <EventsNewsTab fundamentals={fundamentals} />
          )}
        </TabsContent>
      </Tabs>

      {/* Watchlist Modal */}
      <AddToWatchlistModal
        asset={asset}
        open={watchlistModalOpen}
        onOpenChange={setWatchlistModalOpen}
      />
    </div>
  )
}
